import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';

interface PagerData {
  total: number;
  limit: number;
  offset: number;
}

/**
 * 페이지네이션 풋바.
 *
 * Emit:
 *  - page:change   { offset, limit }
 */
export class Pager extends SolaView<PagerData> {
  override events = {
    click: {
      '@first': 'handleFirst',
      '@prev': 'handlePrev',
      '@next': 'handleNext',
      '@last': 'handleLast',
    },
    change: { '@page-size': 'handlePageSize' },
  };

  constructor() {
    super();
    this.data = { total: 0, limit: 50, offset: 0 };
  }

  setState(total: number, limit: number, offset: number): void {
    this.data.total = total;
    this.data.limit = limit;
    this.data.offset = offset;
    this.refresh();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <footer class="pager">
        <div class="pager-info"></div>
        <div class="pager-controls">
          <label class="page-size">
            <span>페이지당</span>
            <select data-action="page-size">
              <option value="25">25</option>
              <option value="50" selected>50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </label>
          <button class="btn-ghost" data-action="first" title="처음">«</button>
          <button class="btn-ghost" data-action="prev" title="이전">‹</button>
          <span class="page-indicator">– / –</span>
          <button class="btn-ghost" data-action="next" title="다음">›</button>
          <button class="btn-ghost" data-action="last" title="마지막">»</button>
        </div>
      </footer>
    `;
  }

  private refresh(): void {
    const root = this._rootElement;
    if (!root) return;

    const { total, limit, offset } = this.data;
    const start = total === 0 ? 0 : offset + 1;
    const end = Math.min(offset + limit, total);
    const currentPage = Math.floor(offset / limit) + 1;
    const totalPages = Math.max(1, Math.ceil(total / limit));

    const info = root.querySelector('.pager-info');
    if (info) {
      info.textContent = `${start.toLocaleString()}–${end.toLocaleString()} / ${total.toLocaleString()} 행`;
    }
    const ind = root.querySelector('.page-indicator');
    if (ind) ind.textContent = `${currentPage} / ${totalPages}`;

    const btn = (sel: string) => root.querySelector<HTMLButtonElement>(sel);
    const atStart = offset === 0;
    const atEnd = end >= total;
    const first = btn('[data-action="first"]');
    const prev = btn('[data-action="prev"]');
    const next = btn('[data-action="next"]');
    const last = btn('[data-action="last"]');
    if (first) first.disabled = atStart;
    if (prev) prev.disabled = atStart;
    if (next) next.disabled = atEnd;
    if (last) last.disabled = atEnd;

    const select = root.querySelector<HTMLSelectElement>('select[data-action="page-size"]');
    if (select && String(limit) !== select.value) select.value = String(limit);
  }

  // ===== 이벤트 =====

  handleFirst(): void {
    if (this.data.offset === 0) return;
    this.emit('page:change', { offset: 0, limit: this.data.limit });
  }
  handlePrev(): void {
    const next = Math.max(0, this.data.offset - this.data.limit);
    if (next === this.data.offset) return;
    this.emit('page:change', { offset: next, limit: this.data.limit });
  }
  handleNext(): void {
    const next = this.data.offset + this.data.limit;
    if (next >= this.data.total) return;
    this.emit('page:change', { offset: next, limit: this.data.limit });
  }
  handleLast(): void {
    const last = Math.max(
      0,
      Math.floor((this.data.total - 1) / this.data.limit) * this.data.limit,
    );
    if (last === this.data.offset) return;
    this.emit('page:change', { offset: last, limit: this.data.limit });
  }
  handlePageSize(_e: Event, target: HTMLElement): void {
    const v = Number((target as HTMLSelectElement).value);
    if (!Number.isFinite(v) || v <= 0) return;
    this.emit('page:change', { offset: 0, limit: v });
  }
}
